// src/features/wf3/popup/W3EditarModal.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  getGerencias,
  getCoordenadores,
  getSubs,
  getSbs,
  validarKm,
  sbKm,
  getAtivosNomes,
  getAtividadesNomes,
} from "../../execucao/api";
import api from "../../../api/axios";
import { editarW3 } from "../api";
import s from "./W3EditarModal.module.css";
import W3EditarRecriacaoModal from "./W3EditarRecriacaoModal";

/* ================= Utils ================= */
const norm = (v) => (v == null ? "" : String(v));
const keyify = (s) => String(s || "").trim().toUpperCase();
const foldSearch = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
const cleanDec = (raw) =>
  String(raw ?? "").replace(",", ".").replace(/[^\d.-]/g, "");
const pickArray = (resp) =>
  Array.isArray(resp)
    ? resp
    : Array.isArray(resp?.data)
    ? resp.data
    : Array.isArray(resp?.data?.data)
    ? resp.data.data
    : [];

const unitFromRow = (row) =>
  row?.unidade ?? row?.unidade_medida ?? row?.medida ?? row?.unid ?? "";

/**
 * Normaliza quantidade vinda do banco:
 * - "24.000" -> "24"
 * - "24,000" -> "24"
 * - "24.500" -> "24.5"
 * - "24.050" -> "24.05"
 * - se não der pra converter, devolve como string original
 */
const normalizeQuantidadeFromDb = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";

  const n = Number(s.replace(",", "."));
  if (!Number.isFinite(n)) return s;

  if (Number.isInteger(n)) return String(n);

  let txt = n.toFixed(3); // 3 casas máx
  txt = txt.replace(/\.?0+$/, ""); // remove zeros à direita
  return txt;
};

const firstNonEmpty = (o, keys, fallback = "") => {
  for (const k of keys) {
    const v = o?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

const canonProgramacao = (p = {}) => ({
  id: p?.id ?? p?.wf_3_id ?? p?.w3_id ?? null,
  data: firstNonEmpty(p, ["data", "day", "data_key", "dataKey"]),
  atividade: firstNonEmpty(p, ["atividade", "atividade_nome"]),
  unidade_medida: firstNonEmpty(
    p,
    ["unidade_medida", "unidade", "medida", "unid"]
  ),
  ativo: firstNonEmpty(p, ["ativo", "ativos", "ativo_nome"]),
  gerencia: firstNonEmpty(p, ["gerencia"]),
  coordenador: firstNonEmpty(p, ["coordenador"]),
  sub: firstNonEmpty(p, ["sub", "subdivisao_id", "sub_id"]),
  kmInicio: firstNonEmpty(p, ["kmInicio", "km_inicio"]),
  kmFinal: firstNonEmpty(p, ["kmFinal", "km_final"]),
  horario_do_intervalo: firstNonEmpty(p, [
    "horario_do_intervalo",
    "horario_intervalo",
    "hora_intervalo",
  ]),
  tempo_do_intervalo: firstNonEmpty(p, [
    "tempo_do_intervalo",
    "tempo_intervalo",
  ]),
  sbOrigem: firstNonEmpty(p, ["sbOrigem", "sb_origem", "patio_origem"]),
  sbDeTrabalho: firstNonEmpty(p, [
    "sbDeTrabalho",
    "sb_de_trabalho",
    "sb_trabalho",
  ]),
  sbEncerramento: firstNonEmpty(p, [
    "sbEncerramento",
    "sb_encerramento",
    "patio_encerramento",
  ]),
  // <<< AQUI entra o normalizador da quantidade vinda do banco
  quantidade: normalizeQuantidadeFromDb(p?.quantidade ?? p?.qtd),
  quantidade_maquinista: firstNonEmpty(p, [
    "quantidade_maquinista",
    "qtd_maquinista",
    "maquinistas",
  ]),
  maquinista: firstNonEmpty(p, ["maquinista"]),
  locomotiva1: firstNonEmpty(p, ["locomotiva1", "locomotiva_1"]),
  locomotiva2: firstNonEmpty(p, ["locomotiva2", "locomotiva_2"]),
  tipo_programacao: firstNonEmpty(p, ["tipo_programacao", "tipoProg", "tipo"]),
  prioridade: String(firstNonEmpty(p, ["prioridade"], "")).toUpperCase(),
  tipo_acionamento: firstNonEmpty(p, ["tipo_acionamento", "acionamento_tipo"]),
  observacao: firstNonEmpty(p, ["observacao"]),
  codigo_unico: firstNonEmpty(p, ["codigo_unico"]),
});

const buildInitialForm = (prog = {}) => ({
  atividade: prog.atividade || "",
  unidade_medida: prog.unidade_medida || "",
  ativo: prog.ativo || "",
  gerencia: prog.gerencia || "",
  coordenador: prog.coordenador || "",
  sub: prog.sub || "",
  kmInicio: prog.kmInicio || "",
  kmFinal: prog.kmFinal || "",
  horario_do_intervalo: prog.horario_do_intervalo || "",
  tempo_do_intervalo: prog.tempo_do_intervalo || "",
  sbOrigem: prog.sbOrigem || "",
  sbDeTrabalho: prog.sbDeTrabalho || "",
  sbEncerramento: prog.sbEncerramento || "",
  quantidade: prog.quantidade ?? "",
  quantidade_maquinista: prog.quantidade_maquinista || "",
  maquinista: prog.maquinista || "",
  locomotiva1: prog.locomotiva1 || "",
  locomotiva2: prog.locomotiva2 || "",
  tipo_programacao: prog.tipo_programacao || "",
  prioridade: prog.prioridade || "",
  tipo_acionamento: prog.tipo_acionamento || "",
  kmValido: true,
  sbMsg: "",
});

/* Normalizador de SB e util de unique+sort */
const normalizeSbItem = (x) =>
  x && typeof x === "object"
    ? String(x.sb ?? x.patio ?? x.nome ?? x.label ?? "").trim()
    : String(x ?? "").trim();

const uniqSorted = (arr) =>
  Array.from(new Set(arr.filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, "pt-BR")
  );

/* Mini diálogo de erro */
function ErrorDialog({ open, message, onClose }) {
  if (!open) return null;
  return (
    <div className={s.errorBackdrop} role="dialog" aria-modal="true">
      <div className={s.errorCard}>
        <div className={s.errorTitle}>Verifique os campos</div>
        <div className={s.errorMsg}>{message}</div>
        <button className={s.errorBtn} onClick={onClose}>
          OK
        </button>
      </div>
    </div>
  );
}

function FieldDropdown({
  value,
  onChange,
  options = [],
  placeholder = "Selecione...",
  invalid = false,
  disabled = false,
  triggerRef: externalTriggerRef = null,
  searchable = false,
  searchPlaceholder = "Digite para filtrar...",
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [query, setQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const safeOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : [])
        .map((opt) => String(opt ?? "").trim())
        .filter(Boolean),
    [options]
  );

  const filteredOptions = useMemo(() => {
    if (!searchable) return safeOptions;
    const foldedQuery = foldSearch(query);
    if (!foldedQuery) return safeOptions;
    return safeOptions.filter((opt) => foldSearch(opt).includes(foldedQuery));
  }, [query, safeOptions, searchable]);

  useEffect(() => {
    if (!externalTriggerRef) return;
    externalTriggerRef.current = triggerRef.current;
  }, [externalTriggerRef, open]);

  useEffect(() => {
    if (!searchable) {
      setQuery("");
      return;
    }
    if (!open) setQuery(value || "");
  }, [open, searchable, value]);

  useEffect(() => {
    if (!open || !searchable) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return undefined;

    const onOutside = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setOpenUp(false);
      setMenuStyle(null);
      return undefined;
    }

    const updatePosition = () => {
      const anchorEl = triggerRef.current || rootRef.current;
      if (!anchorEl) return;

      const rect = anchorEl.getBoundingClientRect();
      const boundary =
        anchorEl.closest(`.${s.content}`) || anchorEl.closest(`.${s.modal}`);
      const boundaryRect = boundary?.getBoundingClientRect?.() || null;
      const gap = 8;
      const itemCount = Math.max(filteredOptions.length, 1);
      const idealHeight = Math.min(Math.max(itemCount * 40, searchable ? 144 : 96), 260);
      const spaceBelow = boundaryRect
        ? boundaryRect.bottom - rect.bottom
        : window.innerHeight - rect.bottom;
      const spaceAbove = boundaryRect ? rect.top - boundaryRect.top : rect.top;
      const nextOpenUp = spaceBelow < idealHeight && spaceAbove > spaceBelow;
      const availableSpace = Math.floor(
        (nextOpenUp ? spaceAbove : spaceBelow) - gap
      );
      const available = Math.max(48, Math.min(idealHeight, availableSpace));
      const menuNaturalHeight = Math.min(
        menuRef.current?.getBoundingClientRect?.().height ||
          menuRef.current?.scrollHeight ||
          idealHeight,
        available
      );
      const anchoredTop = nextOpenUp
        ? rect.top - gap - menuNaturalHeight
        : rect.bottom + gap;

      setOpenUp(nextOpenUp);
      setMenuStyle({
        top: `${Math.round(anchoredTop)}px`,
        left: `${Math.round(rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
        maxHeight: `${available}px`,
      });
    };

    updatePosition();
    const rafId = window.requestAnimationFrame(() => {
      updatePosition();
    });
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [filteredOptions.length, open, searchable]);

  return (
    <div
      ref={rootRef}
      className={[
        s.select,
        open ? s.selectOpen : "",
        disabled ? s.selectDisabled : "",
        invalid ? s.selectInvalid : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {searchable ? (
        open ? (
          <div
            ref={triggerRef}
            className={[s.input, s.selectTrigger, s.selectTriggerSearch, s.selectTriggerEditing]
              .filter(Boolean)
              .join(" ")}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                event.preventDefault();
                setOpen(false);
                inputRef.current?.blur();
              }
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={query}
              disabled={disabled}
              className={[s.selectInlineInput, value || query ? "" : s.selectPlaceholder]
                .filter(Boolean)
                .join(" ")}
              placeholder={searchPlaceholder || placeholder}
              onMouseDown={(event) => {
                if (document.activeElement === inputRef.current) {
                  event.preventDefault();
                  setOpen(false);
                  inputRef.current?.blur();
                }
              }}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        ) : (
          <button
            ref={triggerRef}
            type="button"
            className={[s.input, s.selectTrigger].join(" ")}
            onClick={() => {
              if (disabled) return;
              setQuery(value || "");
              setOpen(true);
            }}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span
              className={[s.selectValue, value ? "" : s.selectPlaceholder]
                .filter(Boolean)
                .join(" ")}
            >
              {value || placeholder}
            </span>
          </button>
        )
      ) : (
        <button
          ref={triggerRef}
          type="button"
          className={[s.input, s.selectTrigger].join(" ")}
          onClick={() => {
            if (!disabled) setOpen((prev) => !prev);
          }}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span
            className={[s.selectValue, value ? "" : s.selectPlaceholder]
              .filter(Boolean)
              .join(" ")}
          >
            {value || placeholder}
          </span>
        </button>
      )}

      {open && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              className={[s.selectMenuFloating, openUp ? s.selectMenuUp : ""]
                .filter(Boolean)
                .join(" ")}
              role="listbox"
              style={menuStyle}
            >
              <div className={s.selectList}>
                {filteredOptions.map((opt) => {
                  const selected = opt === value;
                  return (
                    <button
                      key={opt}
                      type="button"
                      className={[s.selectOption, selected ? s.selectOptionSelected : ""]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => {
                        onChange(opt);
                        setQuery(opt);
                        setOpen(false);
                      }}
                      aria-selected={selected}
                    >
                      {opt}
                    </button>
                  );
                })}

                {!filteredOptions.length && (
                  <div className={s.selectEmpty}>Nenhum resultado encontrado.</div>
                )}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

/* ================= Componente ================= */
export default function W3EditarModal({
  open = false,
  data = {},
  onClose = () => {},
  onSaved = () => {},
}) {
  // dados vindos da ficha (que já vem do banco)
  const prog = useMemo(() => canonProgramacao(data), [data]);

  /* Estado base */
  const [gerencias, setGerencias] = useState([]);
  const [coordenadores, setCoordenadores] = useState([]);
  const [subs, setSubs] = useState([]);
  const [sbsAll, setSbsAll] = useState([]);
  const [sbsCoord, setSbsCoord] = useState([]);
  const [atividades, setAtividades] = useState([]);
  const [ativos, setAtivos] = useState([]);
  const [salvando, setSalvando] = useState(false);

  /* Form (inicia já com o que veio do banco) */
  const [form, setForm] = useState(() => buildInitialForm(prog));

  // sempre que mudar a `data`, recarrega o form
  useEffect(() => {
    setForm(buildInitialForm(prog));
  }, [prog]);

  /* Refs */
  const atividadeRef = useRef(null);
  const ativoRef = useRef(null);
  const qtdRef = useRef(null);

  /* Diálogo de erro */
  const [err, setErr] = useState({ open: false, msg: "" });
  const showErr = (msg, focusRef) => {
    setErr({ open: true, msg });
    setTimeout(() => focusRef?.current?.focus?.(), 0);
  };

  /* Popup de confirmação de recriação */
  const [criticalOpen, setCriticalOpen] = useState(false);

  /* Índices */
  const atividadeIndex = useMemo(() => {
    const map = new Map();
    (atividades || []).forEach((row) => {
      const nome = String(
        row?.atividade || row?.nome || row?.label || row || ""
      ).trim();
      if (!nome) return;
      const k = keyify(nome);
      if (!map.has(k)) map.set(k, { atividade: nome, unidade: unitFromRow(row) });
    });
    return map;
  }, [atividades]);

  const ativoIndex = useMemo(() => {
    const map = new Map();
    (ativos || []).forEach((row) => {
      const nome = String(
        row?.ativos || row?.ativo || row?.nome || row?.label || row || ""
      ).trim();
      if (!nome) return;
      const k = keyify(nome);
      if (!map.has(k)) map.set(k, { ativos: nome });
    });
    return map;
  }, [ativos]);

  const atividadeValida =
    !!(form.atividade && atividadeIndex.has(keyify(form.atividade)));
  const ativoValido = !!(form.ativo && ativoIndex.has(keyify(form.ativo)));

  /* Opções */
  const atividadesOptions = useMemo(() => {
    const set = new Set(
      (atividades || [])
        .map((r) => String(r?.atividade || r || ""))
        .filter(Boolean)
    );
    if (form.atividade) set.add(form.atividade);
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [atividades, form.atividade]);

  const ativosOptions = useMemo(() => {
    const set = new Set(
      (ativos || [])
        .map((r) => String(r?.ativos || r?.ativo || r || ""))
        .filter(Boolean)
    );
    if (form.ativo) set.add(form.ativo);
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [ativos, form.ativo]);

  const subsIds = useMemo(
    () => subs.map((s) => s.subdivisao_id ?? s.sub ?? s.id).filter(Boolean),
    [subs]
  );

  /* Loads (bases) */
  useEffect(() => {
    (async () => {
      try {
        setGerencias((await getGerencias()) || []);
      } catch {}

      try {
        const r = await api.get("/bases/atividades/listar", {
          params: { limit: 100000 },
        });
        setAtividades(pickArray(r));
      } catch {
        try {
          setAtividades(pickArray(await getAtividadesNomes()));
        } catch {
          setAtividades([]);
        }
      }

      try {
        const r = await api.get("/bases/ativos/listar", {
          params: { limit: 100000 },
        });
        setAtivos(pickArray(r));
      } catch {
        try {
          setAtivos(pickArray(await getAtivosNomes()));
        } catch {
          setAtivos([]);
        }
      }

      // Pátios globais
      const tryUrls = [
        "/bases/sb-origem-encerramento/listar",
        "/sb-origem-encerramento/listar",
      ];
      let all = [];
      for (const url of tryUrls) {
        try {
          const resp = await api.get(url, { params: { limit: 100000 } });
          all = all.concat(pickArray(resp).map(normalizeSbItem));
        } catch {}
      }
      all = uniqSorted([
        ...all,
        normalizeSbItem(prog.sbOrigem),
        normalizeSbItem(prog.sbEncerramento),
      ]);
      setSbsAll(all);
    })();
  }, [prog.sbOrigem, prog.sbEncerramento]);

  /* Gerência -> Coordenadores */
  const prevGer = useRef(form.gerencia);
  useEffect(() => {
    const changed = prevGer.current !== form.gerencia;
    prevGer.current = form.gerencia;

    if (!form.gerencia) {
      setCoordenadores([]);
      return;
    }
    getCoordenadores(form.gerencia)
      .then((l) => setCoordenadores(l || []))
      .catch(() => setCoordenadores([]));

    if (changed) {
      setForm((f) => ({
        ...f,
        coordenador: "",
        sub: "",
        kmValido: true,
        sbMsg: "",
      }));
      setSubs([]);
      setSbsCoord([]);
    }
  }, [form.gerencia]);

  /* Coordenador -> SUBs + SBs (info) */
  const prevCoord = useRef(form.coordenador);
  useEffect(() => {
    const changed = prevCoord.current !== form.coordenador;
    prevCoord.current = form.coordenador;

    if (!form.coordenador) {
      setSubs([]);
      setSbsCoord([]);
      return;
    }

    Promise.all([getSubs(form.coordenador), getSbs(form.coordenador)])
      .then(([subsList, sbsList]) => {
        setSubs(subsList || []);
        setSbsCoord(uniqSorted((sbsList || []).map(normalizeSbItem)));
      })
      .catch(() => {
        setSubs([]);
        setSbsCoord([]);
      });

    if (changed)
      setForm((f) => ({ ...f, sub: "", kmValido: true, sbMsg: "" }));
  }, [form.coordenador]);

  /* Prefill pátios */
  useEffect(() => {
    if (!sbsAll.length) return;
    setForm((f) => ({
      ...f,
      sbOrigem: f.sbOrigem || prog.sbOrigem || "",
      sbEncerramento:
        f.sbEncerramento || prog.sbEncerramento || "",
    }));
  }, [sbsAll, prog.sbOrigem, prog.sbEncerramento]);

  /* SUB + KM -> SB de trabalho informativo */
  const prevTriad = useRef("");
  useEffect(() => {
    const sub = form.sub;
    const ki = Number(cleanDec(form.kmInicio));
    const kf = Number(cleanDec(form.kmFinal));
    const key = [
      sub || "",
      Number.isNaN(ki) ? "" : ki,
      Number.isNaN(kf) ? "" : kf,
    ].join("|");
    if (prevTriad.current === key) return;
    prevTriad.current = key;

    if (sub && Number.isFinite(ki) && Number.isFinite(kf)) {
      Promise.all([validarKm(sub, ki, kf), sbKm(sub, ki, kf)])
        .then(([val, sbsList]) => {
          const arr = (Array.isArray(sbsList) ? sbsList : [])
            .map(normalizeSbItem)
            .filter(Boolean);
          setForm((f) => ({
            ...f,
            kmValido: !!val?.existe,
            sbDeTrabalho: val?.existe
              ? arr.length
                ? arr.join(", ")
                : ""
              : "",
            sbMsg: val?.existe
              ? arr.length
                ? ""
                : "Não há SB para o intervalo informado."
              : "KM fora da SUB.",
          }));
        })
        .catch(() =>
          setForm((f) => ({
            ...f,
            kmValido: false,
            sbDeTrabalho: "",
            sbMsg: "Erro ao buscar KM.",
          }))
        );
    } else {
      setForm((f) => ({
        ...f,
        kmValido: true,
        sbDeTrabalho: "",
        sbMsg: "",
      }));
    }
  }, [form.sub, form.kmInicio, form.kmFinal]);

  /* Handlers */
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const handleKmChange = (k, raw) => setField(k, cleanDec(raw));
  const blockWheel = (e) => e.currentTarget.blur();
  const blockArrows = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
  };

  const handleAtividadeChange = (nextValue) => {
    const value =
      typeof nextValue === "string" ? nextValue : nextValue?.target?.value;
    const uni = atividadeIndex.get(keyify(value))?.unidade || "";
    setForm((f) => ({
      ...f,
      atividade: value,
      unidade_medida: uni,
      ativo: "",
      quantidade: "",
    }));
  };

  /* Critérios de alteração */
  const atividadeOriginalKey = keyify(prog.atividade);
  const sbTrabalhoOriginal = String(prog.sbDeTrabalho || "").trim();
  const atividadeAlterada =
    keyify(form.atividade) !== atividadeOriginalKey && !!form.atividade;
  const sbTrabalhoAlterado =
    String(form.sbDeTrabalho || "").trim() !== sbTrabalhoOriginal;

  /* ===== Validação antes de salvar ===== */
  const validarAntesSalvar = () => {
    if (!atividadeValida) {
      showErr("Selecione uma Atividade válida da lista.", atividadeRef);
      return false;
    }

    if (atividadeAlterada) {
      if (!ativoValido) {
        showErr(
          "Ao alterar a atividade, selecione um Ativo válido.",
          ativoRef
        );
        return false;
      }
      const qtdNum = Number(cleanDec(form.quantidade));
      if (!(Number.isFinite(qtdNum) && qtdNum > 0)) {
        showErr(
          "Ao alterar a atividade, informe uma Quantidade maior que zero.",
          qtdRef
        );
        return false;
      }
    }

    return true;
  };

  /* ===== Salvar (chamado depois da confirmação, se precisar) =====
     extraMotivos: objeto opcional vindo do W3EditarRecriacaoModal
     (responsabilidade_alteracao, impacto_alteracao, causa_alteracao,
      justificativa_alteracao, motivo_alteracao)
  */
  const salvar = async (extraMotivos = null) => {
    const kmInicioNum =
      form.kmInicio !== "" ? Number(cleanDec(form.kmInicio)) : null;
    const kmFinalNum =
      form.kmFinal !== "" ? Number(cleanDec(form.kmFinal)) : null;

    const atividadeCanonica = atividadeIndex.get(keyify(form.atividade));
    const ativoCanonico = ativoIndex.get(keyify(form.ativo));

    const payloadBase = {
      data: prog.data || firstNonEmpty(data, ["data", "day", "data_key", "dataKey"]) || null,
      ativo: ativoCanonico?.ativos || form.ativo || null,
      gerencia: form.gerencia || null,
      coordenador: form.coordenador || null,
      sub: form.sub || null,
      kmInicio: kmInicioNum,
      kmFinal: kmFinalNum,
      horario_do_intervalo: form.horario_do_intervalo || "",
      tempo_do_intervalo: form.tempo_do_intervalo || "",
      sbOrigem: form.sbOrigem || "",
      sbEncerramento: form.sbEncerramento || "",
      sbDeTrabalho: form.sbDeTrabalho || "",
      quantidade:
        form.quantidade === "" ? null : Number(cleanDec(form.quantidade)),
      quantidade_maquinista:
        form.quantidade_maquinista === ""
          ? null
          : Number(form.quantidade_maquinista),
      maquinista: form.maquinista || "",
      locomotiva1: form.locomotiva1 || "",
      locomotiva2: form.locomotiva2 || "",
      tipo_programacao: form.tipo_programacao || "",
      prioridade: (form.prioridade || "").toString().trim().toUpperCase(),
      tipo_acionamento: form.tipo_acionamento || "",
    };

    const payload = {
      ...payloadBase,
      ...(atividadeAlterada
        ? {
            atividade: atividadeCanonica?.atividade || form.atividade,
            unidade_medida:
              atividadeCanonica?.unidade || form.unidade_medida || "",
          }
        : {}),
      ...(form.sbDeTrabalho
        ? { sbDeTrabalho: form.sbDeTrabalho || "" }
        : {}),
      ...(extraMotivos || {}),
      id: prog.id,
      codigo_unico: prog.codigo_unico,
      observacao: prog.observacao || "",
      usuario_alteracao:
        JSON.parse(localStorage.getItem("usuario"))?.nome || "SISTEMA",
      // se o modal mandou motivo_alteracao, respeita; senão usa o texto padrão
      motivo_alteracao:
        extraMotivos?.motivo_alteracao ||
        "Edição manual na execução (WF-3)",
    };

    setSalvando(true);
    try {
      const result = await editarW3(payload);
      const r = result?.data ?? result;

      const isSplit = !!r?.recriado;
      const novoCodigo =
        r?.novo_codigo_unico || r?.nova_codigo_unico || null;

      onSaved({
        data: payload.data ?? prog.data ?? null,
        ...payloadBase,
        atividade: payload.atividade ?? prog.atividade,
        unidade_medida: payload.unidade_medida ?? prog.unidade_medida,
        _split: isSplit,
        _nova_codigo_unico: novoCodigo,
        _original_permanece_execucao: !isSplit,
      });
    } catch (e) {
      const dataErr = e?.response?.data;
      const msg =
        dataErr?.message ||
        dataErr?.error ||
        e.message ||
        "Erro ao salvar.";
      showErr(msg);
    } finally {
      setSalvando(false);
    }
  };

  /* Handler do botão Salvar (decide se abre popup ou salva direto) */
  const handleSalvarClick = () => {
    if (!validarAntesSalvar()) return;

    if (atividadeAlterada || sbTrabalhoAlterado) {
      setCriticalOpen(true);
    } else {
      // edição simples: sem motivos extras
      salvar();
    }
  };

  if (!open) return null;

  /* UI */
  return (
    <div className={s.backdrop} onClick={onClose}>
      <div
        className={`${s.modal} ${s.root}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={s.header}>
          <h3 className={s.title}>Editar Programação</h3>
          <button
            className={s.close}
            aria-label="Fechar"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className={s.content}>
          <div className={s.grid}>
            {/* linha 1 */}
            <div className={s.field}>
              <label className={s.label}>Atividade</label>
              <FieldDropdown
                triggerRef={atividadeRef}
                value={form.atividade || ""}
                onChange={handleAtividadeChange}
                options={atividadesOptions}
                invalid={form.atividade && !atividadeValida}
                searchable
                searchPlaceholder="Buscar atividade..."
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Ativo</label>
              <FieldDropdown
                triggerRef={ativoRef}
                value={form.ativo || ""}
                onChange={(nextValue) => setField("ativo", nextValue)}
                options={ativosOptions}
                invalid={atividadeAlterada && form.ativo && !ativoValido}
                searchable
                searchPlaceholder="Buscar ativo..."
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Unidade de Medida</label>
              <input
                className={`${s.input} ${s.inputRO}`}
                readOnly
                value={form.unidade_medida || ""}
              />
            </div>

            {/* linha 2 */}
            <div className={s.field}>
              <label className={s.label}>Gerência</label>
              <FieldDropdown
                value={form.gerencia}
                onChange={(nextValue) => setField("gerencia", nextValue)}
                options={gerencias}
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Coordenador</label>
              <FieldDropdown
                value={form.coordenador}
                onChange={(nextValue) => setField("coordenador", nextValue)}
                options={coordenadores}
                disabled={!form.gerencia}
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>SUB</label>
              <FieldDropdown
                value={form.sub || ""}
                onChange={(nextValue) => setField("sub", nextValue)}
                options={subsIds}
                disabled={!subsIds.length}
              />
            </div>

            {/* linha 3 */}
            <div className={s.field}>
              <label className={s.label}>KM de Início</label>
              <input
                className={s.input}
                type="text"
                inputMode="decimal"
                value={form.kmInicio}
                onChange={(e) => handleKmChange("kmInicio", e.target.value)}
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>KM Final</label>
              <input
                className={s.input}
                type="text"
                inputMode="decimal"
                value={form.kmFinal}
                onChange={(e) => handleKmChange("kmFinal", e.target.value)}
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Quantidade</label>
              <input
                ref={qtdRef}
                className={s.input}
                type="text"
                inputMode="decimal"
                value={form.quantidade}
                onChange={(e) =>
                  setField("quantidade", cleanDec(e.target.value))
                }
                onWheel={blockWheel}
                onKeyDown={blockArrows}
              />
            </div>

            {/* linha 4 */}
            <div className={s.field}>
              <label className={s.label}>Horário do Intervalo</label>
              <input
                className={s.input}
                type="time"
                step={60}
                value={form.horario_do_intervalo || ""}
                onChange={(e) =>
                  setField("horario_do_intervalo", e.target.value)
                }
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Tempo do Intervalo</label>
              <input
                className={s.input}
                type="time"
                step={60}
                value={form.tempo_do_intervalo || ""}
                onChange={(e) =>
                  setField("tempo_do_intervalo", e.target.value)
                }
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Prioridade</label>
              <FieldDropdown
                value={form.prioridade || ""}
                onChange={(nextValue) => setField("prioridade", nextValue)}
                options={["P0", "P1", "P2", "P3", "P4", "P5"]}
                placeholder="Selecionar..."
              />
            </div>

            {/* linha 5 */}
            <div className={s.field}>
              <label className={s.label}>Pátio de Origem</label>
              <FieldDropdown
                value={form.sbOrigem || ""}
                onChange={(nextValue) => setField("sbOrigem", nextValue)}
                options={sbsAll}
                searchable
                searchPlaceholder="Buscar patio de origem..."
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>SB de Trabalho</label>
              <input
                className={`${s.input} ${s.inputRO}`}
                readOnly
                value={form.sbDeTrabalho || ""}
                placeholder="Preenchido por SUB + KM"
              />
              {form.sbMsg && (
                <small className={s.hint}>{form.sbMsg}</small>
              )}
            </div>

            <div className={s.field}>
              <label className={s.label}>Pátio de Encerramento</label>
              <FieldDropdown
                value={form.sbEncerramento || ""}
                onChange={(nextValue) => setField("sbEncerramento", nextValue)}
                options={sbsAll}
                searchable
                searchPlaceholder="Buscar patio de encerramento..."
              />
            </div>

            {/* linha 6 */}
            <div className={s.field}>
              <label className={s.label}>Quantidade Maquinista</label>
              <input
                className={s.input}
                type="number"
                min={0}
                step={1}
                value={form.quantidade_maquinista}
                onChange={(e) =>
                  setField("quantidade_maquinista", e.target.value)
                }
                onWheel={blockWheel}
                onKeyDown={blockArrows}
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Maquinista</label>
              <FieldDropdown
                value={form.maquinista || ""}
                onChange={(nextValue) => setField("maquinista", nextValue)}
                options={["ESCALA", "FIXO", "FIXO + ESCALA"]}
              />
            </div>

            <div className={s.field}>
  <label className={s.label}>Tipo de Acionamento</label>
  <FieldDropdown
    value={form.tipo_acionamento || ""}
    onChange={(nextValue) => setField("tipo_acionamento", nextValue)}
    options={["SBR", "ACIONAMENTO", "AGUARDANDO ACIONAMENTO PCM"]}
  />
</div>

            {/* linha 7 */}
            <div className={s.field}>
              <label className={s.label}>Locomotiva 1</label>
              <input
                className={s.input}
                value={form.locomotiva1}
                onChange={(e) => setField("locomotiva1", e.target.value)}
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Locomotiva 2</label>
              <input
                className={s.input}
                value={form.locomotiva2}
                onChange={(e) => setField("locomotiva2", e.target.value)}
              />
            </div>

            <div className={s.field}>
              <label className={s.label}>Tipo de Programação</label>
              <FieldDropdown
                value={form.tipo_programacao || ""}
                onChange={(nextValue) => setField("tipo_programacao", nextValue)}
                options={["CONTRATO", "OPORTUNIDADE"]}
              />
            </div>
          </div>
        </div>

        <div className={s.actions}>
          <span />
          <div className={s.actionsRight}>
            <button className={s.btn} onClick={onClose}>
              Cancelar
            </button>
            <button
              className={`${s.btn} ${s.btnPrimary}`}
              onClick={handleSalvarClick}
              disabled={salvando || !atividadeValida}
            >
              {salvando ? "Salvando..." : "Salvar alterações"}
            </button>
          </div>
        </div>

        <ErrorDialog
          open={err.open}
          message={err.msg}
          onClose={() => setErr({ open: false, msg: "" })}
        />

        <W3EditarRecriacaoModal
          open={criticalOpen}
          atividadeAlterada={atividadeAlterada}
          sbTrabalhoAlterado={sbTrabalhoAlterado}
          onCancel={() => setCriticalOpen(false)}
          onConfirm={(motivos) => {
            // motivos = { responsabilidade_alteracao, impacto_alteracao, causa_alteracao, justificativa_alteracao, motivo_alteracao }
            setCriticalOpen(false);
            salvar(motivos);
          }}
        />
      </div>
    </div>
  );
}
